import { Injectable, Optional } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { HolidaysPort } from '../../../app/ports/organization/holidays.port';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastV2Service, CoreUIToastDirection } from '../../services/toast.service';
import { OrgSdk } from 'lib-klab-sdks';
import { Holiday } from '../../../domain/models/organization/holiday.model';
import { PageMeta } from '../../../domain/models/organization/page.model';
import { mapSDKHolidayToDomain } from '../../mappers/organization/holiday.mapper';
import { mapSdkPaginationToDomainPageMeta } from '../../mappers/organization/page.mapper';
import { mapOrganizationErrorToMessage } from '../../utils/organization-error.mapper';
import { CreateHolidayRequest } from '../../../domain/models/organization/request/create-holiday.request';
import { UpdateHolidayRequest } from '../../../domain/models/organization/request/update-holiday.request';

@Injectable({ providedIn: 'root' })
export class HolidaysPortAdapter implements HolidaysPort {
    constructor(
        private holidaysApi: OrgSdk.HolidaysService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
    ) { }

    getHolidays(page: number, size: number, year?: number): Observable<{ items: Holiday[]; page: PageMeta }> {
        this.appEvents.loaderVisiblePublish(true, 'holidays');

        return this.holidaysApi.getAllHolidays(page, size, year).pipe(
            map((resp: any) => {
                const pageData = Array.isArray(resp) ? resp[0] : resp;
                return {
                    items: (pageData?.content ?? []).map(mapSDKHolidayToDomain),
                    page: mapSdkPaginationToDomainPageMeta(pageData?.page),
                };
            }),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'holidays')),
        );
    }

    getHolidayById(holidayId: number): Observable<Holiday> {
        this.appEvents.loaderVisiblePublish(true, 'holidays-getById');

        return this.holidaysApi.getHolidayById(holidayId).pipe(
            map((resp) => mapSDKHolidayToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'holidays-getById')),
        );
    }

    createHoliday(payload: CreateHolidayRequest): Observable<Holiday> {
        this.appEvents.loaderVisiblePublish(true, 'holidays-create');

        const sdkPayload: OrgSdk.HolidayCreateRequest = {
            date: payload.date,
            name: payload.name,
        };

        return this.holidaysApi.createHoliday(sdkPayload).pipe(
            map((resp) => mapSDKHolidayToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'holidays-create')),
        );
    }

    updateHoliday(holidayId: number, payload: UpdateHolidayRequest): Observable<Holiday> {
        this.appEvents.loaderVisiblePublish(true, 'holidays-update');

        const sdkPayload: OrgSdk.HolidayUpdateRequest = {
            date: payload.date,
            name: payload.name,
        };

        return this.holidaysApi.updateHoliday(holidayId, sdkPayload).pipe(
            map((resp) => mapSDKHolidayToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'holidays-update')),
        );
    }

    deleteHoliday(holidayId: number): Observable<void> {
        this.appEvents.loaderVisiblePublish(true, 'holidays-delete');

        return this.holidaysApi.deleteHolidayById(holidayId).pipe(
            map(() => void 0),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'holidays-delete')),
        );
    }

    private showErrorToast(err: any) {
        if (!this.toast) return;

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapOrganizationErrorToMessage(err),
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
